import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

const AddPlaytimeDialog = ({ user, paymentGateway, buyOneHour = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [purchaseHours, setPurchaseHours] = useState(buyOneHour ? 1 : 20);
  const [checkoutEmail, setCheckoutEmail] = useState(null);

  function getHoursPrice(hours, paymentType = "payment") {
    let discountedPrice = 0;
    if (paymentType === "subscription") {
      if (hours < 10) {
        discountedPrice = hours * 50;
      } else if (hours < 20) {
        discountedPrice = hours * 45;
      } else {
        discountedPrice = hours * 40;
      }
    } else {
      if (hours < 10) {
        discountedPrice = hours * 52.5;
      } else if (hours < 20) {
        discountedPrice = hours * 47.5;
      } else {
        discountedPrice = hours * 42.5;
      }
    }

    return discountedPrice;
  }

  function getHoursPriceDollars(hours, paymentType = "payment") {
    let discountedPrice = 0;
    if (paymentType === "subscription") {
      if (hours < 10) {
        discountedPrice = hours * 0.75;
      } else if (hours < 20) {
        discountedPrice = hours * 0.7;
      } else {
        discountedPrice = hours * 0.65;
      }
    } else {
      if (hours < 10) {
        discountedPrice = hours * 0.75;
      } else if (hours < 20) {
        discountedPrice = hours * 0.7;
      } else {
        discountedPrice = hours * 0.65;
      }
    }

    return discountedPrice;
  }

  async function handleHoursBuy(quantity, payment_gateway, isHourlyPlan) {
    if (!user) {
      navigate("/signup");
    }
    setCheckoutLoading(true);
    await fetch(
      payment_gateway === "stripe"
        ? process.env.NODE_ENV === "development"
          ? "https://1ku3nifl2e.execute-api.ap-south-1.amazonaws.com/default/getStripeCheckout-dev-hello"
          : "https://v1wapno6ch.execute-api.ap-south-1.amazonaws.com/prod/getStripeCheckout-prod-hello"
        : process.env.NODE_ENV === "development"
          ? "https://9mljq6r4e3.execute-api.ap-south-1.amazonaws.com/default/getRazorpayCheckout-dev-hello"
          : "https://d58cycm35h.execute-api.ap-south-1.amazonaws.com/default/getRazorpayCheckout-prod-hello",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: user.signInUserSession.idToken.jwtToken,
        },
        body: JSON.stringify({
          payment_mode: "payment",
          currency: "usd",
          products: [
            {
              priceId: null,
              isHourlyPlan: isHourlyPlan,
              adjustableQuantity: false,
              quantity: quantity,
              email: checkoutEmail,
              vmMachineType: null,
              vmPaymentPeriod: null,
              vmPaymentType: "payment",
              hoursPaymentPeriod: null,
              hoursPaymentType: "payment",
            },
          ],
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else if (data.order_id && payment_gateway === "razorpay") {
          const options = {
            key: data.key_id,
            amount: data.amount, // Amount should be in currency subunits
            currency: "INR",
            name: "GameAway",
            order_id: data.order_id,
            handler: function (response) {
              navigate("/confirmation");
            },
            prefill: {
              email: checkoutEmail,
            },
            theme: {
              color: "#231955",
            },
          };
          const paymentObject = new window.Razorpay(options);
          paymentObject.open();
        } else if (data.subscription_id && payment_gateway === "razorpay") {
          const options = {
            key: data.key_id, // Enter the Key ID generated from the Razorpay Dashboard
            subscription_id: data.subscription_id,
            name: "GameAway",
            handler: function (response) {
              navigate("/confirmation");
            },
            prefill: {
              email: checkoutEmail,
            },
            theme: {
              color: "#231955",
            },
          };
          const paymentObject = new window.Razorpay(options);
          paymentObject.open();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setCheckoutLoading(false);
      });
  }

  return (
    <Dialog>
      <DialogTrigger asChild to="/pricing">
        {!buyOneHour ?
          (<Button
            variant="default"
            asChild
            className="rounded-full w-min cursor-pointer"
          >
            <div>
              <Plus />
              <div>{t("add_playtime")}</div>
            </div>
          </Button>)
          :
          (<Button
            variant="outline"
            className="flex flex-col items-start gap-2 bg-[var(--card)] rounded-2xl p-6 h-full text-left hover:bg-[var(--button-background)]"
            onClick={() => console.log("quick_play_game")}
          >
            <div className="flex gap-4 text-neutral-200">
              <div className="grow">
                <div className="text-xl font-semibold text-wrap">⚡ {t("play_preinstalled_games")}</div>
                <div className="text-sm font-medium text-wrap">
                  {t("preinstalled_games_note")}
                  <div className="font-bold">You get a Quick Play PC with 1 hour playtime</div>
                </div>
              </div>
              {!paymentGateway ? (
                <div>
                  <div className="flex items-baseline text-foreground">
                    <Skeleton className="h-10 w-2/3" />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div>
                    <span className="text-xl font-semibold">{paymentGateway === "razorpay" ? "₹" : "$"}</span>
                    <span className="text-3xl font-bold">
                      {paymentGateway === "razorpay" ? "52.5" : "0.75"}
                    </span>
                  </div>
                  {paymentGateway === "razorpay" ?
                    <span className="ml-1 text-sm text-muted-foreground">
                      + 18% GST
                    </span>
                    : <></>}
                </div>)}
            </div>
          </Button>
          )}
      </DialogTrigger>
      <DialogContent className="bg-[var(--ring-offset)] rounded-2xl sm:rounded-2xl shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)] shadow-lg border border-[var(--button-background)]">
        <DialogHeader>
          <DialogTitle className="text-neutral-50 text-2xl font-semibold">
            {buyOneHour ? "Try for 1 hour" : "Add playtime"}
          </DialogTitle>
          <DialogDescription className="self-stretch text-[var(--muted-foreground)] text-sm font-medium">
            {buyOneHour
              ? "Try GameAway for 1 hour to experience lag-free gaming on our Quick Play PC"
              : "Purchase additional non-expiring hours for your Private Gaming PC or Quick Play PC."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 w-full">
          <div className="flex justify-between items-start">
            <span className="text-base text-muted-foreground">
              Purchase hours
            </span>
            <span className="text-base font-semibold text-foreground">
              {purchaseHours} hours
            </span>
          </div>

          <Slider
            value={[purchaseHours]}
            onValueChange={(v) => setPurchaseHours(v[0])}
            max={50}
            min={1}
            step={1}
            className="w-full cursor-pointer"
          />

          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground">1h</span>
            <span className="text-sm text-muted-foreground">50h</span>
          </div>
          <div className="flex justify-between items-start">
            <div className="text-neutral-50 text-xl font-medium leading-7">
              Total
            </div>
            {!paymentGateway ? (
              <div>
                <Skeleton className="h-5 w-14" />
              </div>
            ) : paymentGateway === "stripe" ? (
              <div className="text-neutral-50 text-base font-medium leading-normal">
                <span
                  className={cn(
                    purchaseHours > 10 ? "inline-flex" : "hidden",
                    "line-through pr-2"
                  )}
                >
                  ${(purchaseHours * 0.75).toFixed(2)}
                </span>
                ${getHoursPriceDollars(purchaseHours, "payment").toFixed(2)}
              </div>
            ) : (
              <div className="text-neutral-50 text-base font-medium leading-normal">
                <span
                  className={cn(
                    purchaseHours > 10 ? "inline-flex" : "hidden",
                    "line-through pr-2"
                  )}
                >
                  ₹{purchaseHours * 52.5}
                </span>
                ₹{getHoursPrice(purchaseHours, "payment")} + 18% GST
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          {checkoutLoading ? (
            <Button disabled className="rounded-full">
              <Loader2 className="animate-spin" />
              Loading
            </Button>
          ) : (
            <Button
              className="rounded-full"
              type="submit"
              onClick={() =>
                handleHoursBuy(purchaseHours, paymentGateway, true)
              }
            >
              Pay Now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPlaytimeDialog;
